import { ChevronDown } from "lucide-react";
import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { cn } from "./cn";

const control =
  "w-full rounded-md border border-line-strong bg-white text-[15px] text-ink shadow-[0_1px_0_rgb(255_255_255/0.85)_inset] placeholder:text-muted/65 transition-[border-color,box-shadow,background-color] hover:border-brand-line focus:border-brand focus:bg-surface focus:outline-none focus:ring-3 focus:ring-brand/18 disabled:bg-ground disabled:text-muted aria-invalid:border-bad aria-invalid:focus:ring-bad/18";

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(control, "h-11 px-3", className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(control, "min-h-24 px-3 py-2 leading-relaxed", className)} {...props} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select className={cn(control, "h-11 appearance-none pr-9 pl-3", className)} {...props}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted" />
    </div>
  );
}

/** 「必須」/「任意」 tag in front of a field label, as Japanese forms do. */
export function RequiredTag({ required }: { required?: boolean }) {
  return required ? (
    <span aria-hidden="true" className="rounded-sm bg-bad px-1.5 py-px text-[11px] leading-5 font-bold tracking-normal text-white">必須</span>
  ) : (
    <span aria-hidden="true" className="rounded-sm bg-line px-1.5 py-px text-[11px] leading-5 font-bold tracking-normal text-ink-2">任意</span>
  );
}

/**
 * A labelled form row. On wide screens the label sits in a left column,
 * the classic Japanese form table; below sm it stacks.
 */
export function Field({
  label,
  required,
  hint,
  error,
  children,
  className,
  htmlFor,
}: {
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: string | null;
  children: (id: string) => ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  const auto = useId();
  const id = htmlFor ?? auto;
  return (
    <div className={cn("grid gap-2 border-b border-line py-4 last:border-b-0 sm:grid-cols-[13rem_minmax(0,1fr)] sm:gap-6", className)}>
      <label htmlFor={id} className="flex items-start gap-2 pt-0 text-sm font-bold text-ink sm:pt-2.5">
        <RequiredTag required={required} />
        <span className="min-w-0">{label}</span>
      </label>
      <div className="min-w-0">
        {children(id)}
        {error ? (
          <p role="alert" className="mt-1.5 text-[13px] font-bold text-bad">
            {error}
          </p>
        ) : (
          hint && <p className="mt-1.5 text-[13px] text-muted">{hint}</p>
        )}
      </div>
    </div>
  );
}

/** Compact stacked field for dense panels and dialogs. */
export function StackField({ label, required, hint, error, children }: { label: ReactNode; required?: boolean; hint?: ReactNode; error?: string | null; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="flex items-center gap-2 text-sm font-bold">
        {required !== undefined && <RequiredTag required={required} />}
        {label}
      </label>
      {children(id)}
      {error ? <p role="alert" className="text-[13px] font-bold text-bad">{error}</p> : hint && <p className="text-[13px] text-muted">{hint}</p>}
    </div>
  );
}

export function Checkbox({ label, className, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cn("inline-flex cursor-pointer items-start gap-2 text-sm", className)}>
      <input type="checkbox" className="mt-1 size-4 shrink-0 rounded border-line-strong accent-brand" {...props} />
      <span>{label}</span>
    </label>
  );
}
